import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelDrawing,
  checkpointDrawing,
  commitDrawing,
  fetchDrawingState,
  newClientId,
  openDrawing,
  removeDrawing,
} from "./drawingAnswerApi";
import { emptyDrawing } from "./drawingFormat";
import { clearRecovery, hasUnsyncedChanges, readRecovery, writeRecovery } from "./drawingRecovery";

// Page-level drawing answers for one attempt.
//
// - Save drawing commits the drawing as the answer.
// - While the pad is open, changed strokes are checkpointed to the server
//   (3 s after the last change, at most every 10 s) for recovery and expiry.
// - Cancel discards the edit session on the server. It only counts as done
//   once the server confirms; until then the pad shows "cancellation pending".
// - Every change is also kept on this device until the server has it.
//
// All drawing requests go through one queue, so they reach the server in
// the order they were made.

export const CHECKPOINT_DEBOUNCE_MS = 3000;
export const CHECKPOINT_MAX_WAIT_MS = 10000;
export const DRAWING_RETRY_MS = 5000;

const ATTEMPT_ENDED = ["attempt-expired", "assignment-closed", "attempt-not-active"];
const UNCONFIRMED = (error) => error?.status === 0 || error?.status >= 500 || error?.code === "drawing-save-uncertain";

export default function useDrawingAnswers({ uid, attemptId, questionIds = [], active, onAttemptEnded }) {
  const [available, setAvailable] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [summaries, setSummaries] = useState({});
  const [pad, setPad] = useState(null);
  const [notices, setNotices] = useState({});
  const [busy, setBusy] = useState({});

  const queueRef = useRef(Promise.resolve());
  const padRef = useRef(null);
  const summariesRef = useRef({});
  const timersRef = useRef({});
  const endedRef = useRef(onAttemptEnded);
  const questionIdsRef = useRef(questionIds);
  endedRef.current = onAttemptEnded;
  questionIdsRef.current = questionIds;

  const enqueue = useCallback((operation) => {
    const run = queueRef.current.then(operation, operation);
    queueRef.current = run.catch(() => {});
    return run;
  }, []);

  const replacePad = useCallback((next) => {
    padRef.current = next;
    setPad(next ? { ...next } : null);
  }, []);
  const updatePad = useCallback((patch) => {
    if (!padRef.current) return;
    replacePad({ ...padRef.current, ...patch });
  }, [replacePad]);

  const setSummary = useCallback((questionId, patch) => {
    const current = summariesRef.current[questionId] || { revision: 0, hasDrawing: false, committedAt: null, draft: null, editing: null };
    summariesRef.current = { ...summariesRef.current, [questionId]: { ...current, ...patch } };
    setSummaries(summariesRef.current);
  }, []);

  const notify = useCallback((questionId, tone, text) => {
    setNotices((previous) => ({ ...previous, [questionId]: text ? { tone, text } : undefined }));
  }, []);

  const clearTimers = useCallback(() => {
    Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
    timersRef.current = {};
  }, []);

  const persist = useCallback((state, extra = {}) => writeRecovery(uid, attemptId, state.questionId, {
    editSessionId: state.editSessionId,
    baseRevision: state.baseRevision,
    sequence: state.sequence,
    ackedSequence: state.ackedSequence,
    drawing: state.drawing,
    cancelRequestId: state.cancelRequestId || null,
    ...extra,
  }), [uid, attemptId]);

  const attemptEnded = useCallback((error) => {
    if (!ATTEMPT_ENDED.includes(error?.code)) return false;
    clearTimers();
    endedRef.current?.(error.code);
    return true;
  }, [clearTimers]);

  const refresh = useCallback(async () => {
    if (!attemptId || !uid) return null;
    try {
      const state = await enqueue(() => fetchDrawingState(attemptId));
      summariesRef.current = state.drawings;
      setSummaries(state.drawings);
      setAvailable(state.enabled);
      setLoaded(true);
      return state;
    } catch (error) {
      // An older server without drawing routes, or no connection: drawing
      // stays unavailable and `loaded` stays false, so submission does not
      // claim to know the drawing revisions.
      return null;
    }
  }, [attemptId, uid, enqueue]);

  // ── Checkpoints ─────────────────────────────────────────────────────────
  const runCheckpointRef = useRef(null);
  const scheduleCheckpoint = useCallback(() => {
    const timers = timersRef.current;
    clearTimeout(timers.debounce);
    timers.debounce = setTimeout(() => runCheckpointRef.current(), CHECKPOINT_DEBOUNCE_MS);
    if (!timers.maxWait) timers.maxWait = setTimeout(() => runCheckpointRef.current(), CHECKPOINT_MAX_WAIT_MS);
  }, []);

  const handleSessionError = useCallback((error) => {
    if (attemptEnded(error)) return true;
    if (error?.code === "drawing-edit-session-closed") {
      clearTimers();
      updatePad({ phase: "superseded", reason: error.details?.reason || "closed", syncState: "failed" });
      return true;
    }
    return false;
  }, [attemptEnded, clearTimers, updatePad]);

  const runCheckpoint = useCallback(async () => {
    const timers = timersRef.current;
    clearTimeout(timers.debounce);
    clearTimeout(timers.maxWait);
    clearTimeout(timers.retry);
    timers.debounce = null;
    timers.maxWait = null;
    const state = padRef.current;
    if (!state || state.phase !== "editing" || state.sequence <= state.ackedSequence) return;
    const { questionId, editSessionId, sequence, drawing } = state;
    updatePad({ syncState: "saving" });
    try {
      const result = await enqueue(() => checkpointDrawing({ attemptId, questionId, editSessionId, sequence, drawing }));
      const current = padRef.current;
      if (!current || current.editSessionId !== editSessionId) return;
      const ackedSequence = Math.max(current.ackedSequence, result.acceptedSequence);
      persist({ ...current, ackedSequence });
      updatePad({
        ackedSequence,
        syncState: current.sequence > ackedSequence ? "pending" : "saved",
        lastSavedAt: result.acceptedAt || current.lastSavedAt,
      });
      if (current.sequence > ackedSequence) scheduleCheckpoint();
    } catch (error) {
      if (handleSessionError(error)) return;
      if (padRef.current?.editSessionId !== editSessionId) return;
      if (error?.code === "invalid-drawing") {
        updatePad({ syncState: "failed", message: "This drawing is too detailed to save. Undo or erase some strokes." });
        return;
      }
      updatePad({ syncState: "offline" });
      timersRef.current.retry = setTimeout(() => runCheckpointRef.current(), DRAWING_RETRY_MS);
    }
  }, [attemptId, enqueue, handleSessionError, persist, scheduleCheckpoint, updatePad]);
  runCheckpointRef.current = runCheckpoint;

  // ── Opening ─────────────────────────────────────────────────────────────
  const openPad = useCallback(async (questionId, { takeover = false, keepDrawing = null } = {}) => {
    if (!attemptId || !uid) return;
    clearTimers();
    notify(questionId, null, null);
    const local = readRecovery(uid, attemptId, questionId);
    const serverSession = summariesRef.current[questionId]?.editing?.editSessionId;
    const resumeId = local && !local.cancelRequestId && local.editSessionId === serverSession ? local.editSessionId : null;
    const editSessionId = resumeId || newClientId();
    replacePad({ questionId, editSessionId, phase: "opening", syncState: "saved", message: null });
    let opened;
    try {
      opened = await enqueue(() => openDrawing({ attemptId, questionId, editSessionId, takeover }));
    } catch (error) {
      if (padRef.current?.editSessionId !== editSessionId) return;
      if (attemptEnded(error)) return;
      if (error?.code === "drawing-editing-elsewhere") {
        updatePad({ phase: "takeover", other: error.details?.drawing || null, keepDrawing });
        return;
      }
      updatePad({
        phase: "error",
        message: error?.code === "drawing-disabled"
          ? "Drawing answers are not available right now."
          : UNCONFIRMED(error)
            ? "We couldn't open the drawing because StudyDen can't be reached. Check your connection and try again."
            : error?.message || "The drawing could not be opened.",
      });
      return;
    }
    if (padRef.current?.editSessionId !== editSessionId) return;

    let drawing;
    let sequence = opened.acceptedSequence;
    let notice = null;
    let recoveryOffer = null;
    if (opened.resumed && local?.editSessionId === editSessionId && local.sequence > opened.acceptedSequence) {
      ({ drawing, sequence } = local);
      notice = "Restored changes from this device that have not reached StudyDen yet.";
    } else if (keepDrawing) {
      drawing = keepDrawing;
      sequence = opened.acceptedSequence + 1;
    } else {
      drawing = opened.draft?.drawing || opened.committed?.drawing || emptyDrawing();
      if (opened.draft) {
        notice = opened.tookOver
          ? "Loaded the last draft your other device or window saved to StudyDen."
          : "Loaded your unsaved draft. Save drawing to keep it as your answer, or Cancel to discard it.";
      }
      if (local && local.editSessionId !== editSessionId && hasUnsyncedChanges(local) && !local.cancelRequestId) {
        recoveryOffer = { drawing: local.drawing, updatedAt: local.updatedAt, olderBase: local.baseRevision !== opened.revision };
      }
    }
    const state = {
      questionId,
      editSessionId,
      phase: "editing",
      baseRevision: opened.revision,
      ackedSequence: opened.acceptedSequence,
      sequence,
      drawing,
      initialDrawing: drawing,
      lastSavedAt: opened.draft?.acceptedAt || null,
      syncState: sequence > opened.acceptedSequence ? "pending" : "saved",
      notice,
      recoveryOffer,
      message: null,
      commitRequest: null,
      cancelRequestId: null,
      localStored: true,
    };
    // A recovery copy from another session is kept until the student decides.
    if (!recoveryOffer) state.localStored = persist(state);
    replacePad(state);
    setSummary(questionId, { editing: { editSessionId, openedAt: new Date().toISOString() } });
    if (sequence > opened.acceptedSequence) scheduleCheckpoint();
  }, [attemptId, uid, attemptEnded, clearTimers, enqueue, notify, persist, replacePad, scheduleCheckpoint, setSummary, updatePad]);

  const changeDrawing = useCallback((drawing) => {
    const state = padRef.current;
    if (!state || !["editing"].includes(state.phase)) return;
    const next = { ...state, drawing, sequence: state.sequence + 1, syncState: "pending", message: null };
    next.localStored = persist(next);
    replacePad(next);
    scheduleCheckpoint();
  }, [persist, replacePad, scheduleCheckpoint]);

  const adoptRecovered = useCallback(() => {
    const state = padRef.current;
    if (!state?.recoveryOffer) return;
    const next = {
      ...state,
      drawing: state.recoveryOffer.drawing,
      initialDrawing: state.recoveryOffer.drawing,
      sequence: state.sequence + 1,
      recoveryOffer: null,
      syncState: "pending",
    };
    next.localStored = persist(next);
    replacePad(next);
    scheduleCheckpoint();
  }, [persist, replacePad, scheduleCheckpoint]);

  const discardRecovered = useCallback(() => {
    const state = padRef.current;
    if (!state?.recoveryOffer) return;
    const next = { ...state, recoveryOffer: null };
    next.localStored = persist(next);
    replacePad(next);
  }, [persist, replacePad]);

  // ── Save drawing ────────────────────────────────────────────────────────
  const saveDrawing = useCallback(async ({ hasInk = true } = {}) => {
    const state = padRef.current;
    if (!state || !["editing", "conflict"].includes(state.phase)) return;
    if (!hasInk) {
      updatePad({ message: "Draw your answer before saving. To delete a saved drawing, close the pad and use Remove." });
      return;
    }
    clearTimers();
    const requestId = state.commitRequest?.sequence === state.sequence && state.commitRequest.baseRevision === state.baseRevision
      ? state.commitRequest.id
      : newClientId();
    const { questionId, editSessionId, baseRevision, drawing } = state;
    updatePad({ phase: "saving", message: null, commitRequest: { id: requestId, sequence: state.sequence, baseRevision } });
    try {
      const result = await enqueue(() => commitDrawing({ attemptId, questionId, editSessionId, requestId, baseRevision, drawing }));
      clearRecovery(uid, attemptId, questionId);
      replacePad(null);
      setSummary(questionId, {
        revision: result.revision, hasDrawing: true, committedAt: new Date().toISOString(), draft: null, editing: null,
      });
      notify(questionId, "success", "Drawing saved.");
    } catch (error) {
      if (handleSessionError(error)) return;
      if (error?.code === "drawing-conflict") {
        updatePad({ phase: "conflict", conflict: error.details?.drawing || null });
      } else if (error?.code === "drawing-blank") {
        updatePad({ phase: "editing", message: "A blank drawing can't be saved as an answer." });
      } else {
        updatePad({
          phase: "editing",
          syncState: UNCONFIRMED(error) ? "offline" : state.syncState,
          message: UNCONFIRMED(error)
            ? "Not saved yet — StudyDen couldn't be reached. Your drawing is still here and kept on this device. Try Save drawing again."
            : error?.message || "The drawing could not be saved.",
        });
      }
    }
  }, [attemptId, uid, clearTimers, enqueue, handleSessionError, notify, replacePad, setSummary, updatePad]);

  const replaceWithMine = useCallback(() => {
    const state = padRef.current;
    if (state?.phase !== "conflict" || !state.conflict) return;
    replacePad({ ...state, phase: "editing", baseRevision: state.conflict.revision, conflict: null, commitRequest: null });
    return saveDrawing();
  }, [replacePad, saveDrawing]);

  // ── Cancel ──────────────────────────────────────────────────────────────
  const retryCancelRef = useRef(null);
  const cancelPad = useCallback(async () => {
    const state = padRef.current;
    if (!state) return;
    if (["takeover", "error"].includes(state.phase)) {
      // No edit session of ours exists on the server, so nothing to discard.
      replacePad(null);
      return;
    }
    if (!["editing", "conflict", "cancel-pending", "superseded"].includes(state.phase)) return;
    clearTimers();
    const cancelRequestId = state.cancelRequestId || newClientId();
    persist({ ...state, cancelRequestId });
    const { questionId, editSessionId } = state;
    updatePad({ phase: "cancelling", cancelRequestId, message: null });
    try {
      const result = await enqueue(() => cancelDrawing({ attemptId, questionId, editSessionId, requestId: cancelRequestId }));
      clearRecovery(uid, attemptId, questionId);
      replacePad(null);
      if (result.status === "session-closed" && result.reason === "committed") {
        notify(questionId, "info", "Your drawing had already been saved before you cancelled.");
      } else if (result.status === "session-closed" && result.reason === "superseded") {
        notify(questionId, "info", "This drawing is being edited on another device or window, so nothing was changed here.");
      } else {
        notify(questionId, "info", "Changes discarded. Your previously saved drawing is unchanged.");
      }
      await refresh();
    } catch (error) {
      if (attemptEnded(error)) return;
      if (padRef.current?.editSessionId !== editSessionId) return;
      updatePad({ phase: "cancel-pending" });
      timersRef.current.retry = setTimeout(() => retryCancelRef.current(), DRAWING_RETRY_MS);
    }
  }, [attemptId, uid, attemptEnded, clearTimers, enqueue, notify, persist, refresh, replacePad, updatePad]);
  retryCancelRef.current = cancelPad;

  const keepEditing = useCallback(() => {
    const state = padRef.current;
    if (state?.phase !== "cancel-pending") return;
    clearTimers();
    const next = { ...state, phase: "editing", cancelRequestId: null };
    persist(next);
    replacePad(next);
    if (next.sequence > next.ackedSequence) scheduleCheckpoint();
  }, [clearTimers, persist, replacePad, scheduleCheckpoint]);

  // Close the pad without discarding anything: used after another device has
  // taken over. Local changes stay on this device for later recovery.
  const closePad = useCallback(() => {
    clearTimers();
    replacePad(null);
    refresh();
  }, [clearTimers, refresh, replacePad]);

  const takeOver = useCallback(() => {
    const state = padRef.current;
    if (!state) return;
    const keepDrawing = state.phase === "superseded" ? state.drawing : state.keepDrawing || null;
    openPad(state.questionId, { takeover: true, keepDrawing });
  }, [openPad]);

  // ── Card actions ────────────────────────────────────────────────────────
  const removeCommitted = useCallback(async (questionId) => {
    const summary = summariesRef.current[questionId];
    if (!summary?.hasDrawing) return;
    setBusy((previous) => ({ ...previous, [questionId]: "removing" }));
    try {
      const result = await enqueue(() => removeDrawing({
        attemptId, questionId, requestId: newClientId(), baseRevision: summary.revision,
      }));
      setSummary(questionId, { revision: result.revision, hasDrawing: false, committedAt: null });
      notify(questionId, "info", "Drawing removed.");
    } catch (error) {
      if (attemptEnded(error)) return;
      notify(questionId, "error", error?.code === "drawing-conflict" || error?.code === "drawing-editing-elsewhere"
        ? "This drawing was changed or opened in another window. Check it before removing it."
        : UNCONFIRMED(error)
          ? "The drawing hasn't been removed — StudyDen couldn't be reached. Try again."
          : error?.message || "The drawing could not be removed.");
      await refresh();
    } finally {
      setBusy((previous) => ({ ...previous, [questionId]: undefined }));
    }
  }, [attemptId, attemptEnded, enqueue, notify, refresh, setSummary]);

  // Discards a recoverable draft (from this or another device) without
  // opening the pad: take over its session, then cancel it.
  const discardDraft = useCallback(async (questionId) => {
    setBusy((previous) => ({ ...previous, [questionId]: "discarding" }));
    const editSessionId = newClientId();
    try {
      await enqueue(() => openDrawing({ attemptId, questionId, editSessionId, takeover: true }));
      await enqueue(() => cancelDrawing({ attemptId, questionId, editSessionId, requestId: newClientId() }));
      clearRecovery(uid, attemptId, questionId);
      notify(questionId, "info", "Unsaved drawing discarded. Your previously saved drawing is unchanged.");
    } catch (error) {
      if (attemptEnded(error)) return;
      notify(questionId, "error", UNCONFIRMED(error)
        ? "The draft hasn't been discarded — StudyDen couldn't be reached. Try again."
        : error?.message || "The draft could not be discarded.");
    } finally {
      setBusy((previous) => ({ ...previous, [questionId]: undefined }));
      await refresh();
    }
  }, [attemptId, uid, attemptEnded, enqueue, notify, refresh]);

  // Last chance to checkpoint before the timer's own finalization. The server
  // refuses anything that arrives after its cutoff.
  const flushForExpiry = useCallback(async () => {
    try { await runCheckpointRef.current?.(); } catch (_error) { /* finalization proceeds */ }
  }, []);

  // ── Lifecycle ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || !attemptId || !uid) return undefined;
    let cancelled = false;
    (async () => {
      const state = await refresh();
      if (cancelled || !state) return;
      // A cancellation the server never confirmed is retried after a reload,
      // but only while the same edit session is still the open one.
      for (const questionId of questionIdsRef.current) {
        const local = readRecovery(uid, attemptId, questionId);
        if (!local?.cancelRequestId) continue;
        if (state.drawings[questionId]?.editing?.editSessionId !== local.editSessionId) {
          clearRecovery(uid, attemptId, questionId);
          continue;
        }
        try {
          await enqueue(() => cancelDrawing({
            attemptId, questionId, editSessionId: local.editSessionId, requestId: local.cancelRequestId,
          }));
          clearRecovery(uid, attemptId, questionId);
          notify(questionId, "info", "Your earlier cancellation has now reached StudyDen. The previously saved drawing is unchanged.");
        } catch (error) {
          if (attemptEnded(error)) return;
          notify(questionId, "error", "An earlier cancellation still hasn't reached StudyDen. Open the drawing to try again.");
        }
      }
      if (!cancelled) await refresh();
    })();
    return () => { cancelled = true; };
  }, [active, attemptId, uid, refresh, enqueue, notify, attemptEnded]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const hasDrawing = useCallback((questionId) => Boolean(summaries[questionId]?.hasDrawing), [summaries]);
  const openDraftIds = Object.entries(summaries)
    .filter(([, summary]) => summary.draft || summary.editing)
    .map(([questionId]) => questionId);
  const drawingRevisions = useCallback(() => Object.fromEntries(
    Object.entries(summariesRef.current).map(([questionId, summary]) => [questionId, summary.revision])
  ), []);

  return {
    available,
    loaded,
    summaries,
    pad,
    notices,
    busy,
    hasDrawing,
    openDraftIds,
    drawingRevisions,
    refresh,
    openPad,
    changeDrawing,
    adoptRecovered,
    discardRecovered,
    saveDrawing,
    replaceWithMine,
    cancelPad,
    keepEditing,
    closePad,
    takeOver,
    removeCommitted,
    discardDraft,
    flushForExpiry,
    dismissNotice: (questionId) => notify(questionId, null, null),
  };
}
